/**
 * Vendors.jsx - Vendor list page with filtering, search, and creation.
 *
 * Displays a filterable, searchable DataTable of all vendors with:
 *   - Filter bar: search, category, status, risk rating, approval status, strategic tier
 *   - Clear filters button
 *   - Vendors count display
 *   - Add vendor button (opens modal)
 *   - Export button
 *   - Clickable rows navigate to vendor detail
 *
 * Data is fetched via getVendors() from ../api/vendors.
 * New vendors are created via createVendor() from ../api/vendors.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Download, X, Filter } from "lucide-react";
import { getVendors, createVendor } from "../api/vendors";
import { useCompany } from "../company/CompanyContext";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import Loading from "../components/Loading";
import { useToast } from "../components/Toast";

/* ------------------------------------------------------------------ */
/* Filter options                                                      */
/* ------------------------------------------------------------------ */

const CATEGORY_OPTIONS = [
  "",
  "Raw Materials",
  "Packaging",
  "Services",
  "Equipment",
  "Logistics",
  "MRO Supplies",
  "Chemicals",
  "Electronics",
];

const STATUS_OPTIONS = ["", "Active", "Inactive", "On Hold", "Blacklisted"];

const RISK_OPTIONS = ["", "Low", "Medium", "High", "Critical"];

const APPROVAL_OPTIONS = ["", "Approved", "Pending", "Pending Approval", "Rejected"];

const TIER_OPTIONS = ["", "Strategic", "Preferred", "Approved", "Probationary", "New"];

/* ------------------------------------------------------------------ */
/* Column definitions                                                  */
/* ------------------------------------------------------------------ */

const VENDOR_COLUMNS = [
  { key: "vendorNo", label: "Vendor No." },
  { key: "name", label: "Name" },
  { key: "category", label: "Category" },
  {
    key: "tier",
    label: "Tier",
    render: (val) => (val ? <Badge text={val} /> : "\u2014"),
  },
  {
    key: "status",
    label: "Status",
    render: (val) => <Badge text={val || "Active"} />,
  },
  {
    key: "approval",
    label: "Approval",
    render: (val) => <Badge text={val || "Approved"} />,
  },
  {
    key: "risk",
    label: "Risk",
    render: (val) => {
      if (!val) return "\u2014";
      const variant =
        val === "Critical" ? "bad" : val === "High" ? "bad" : val === "Medium" ? "warn" : "good";
      return <Badge text={val} variant={variant} />;
    },
  },
  { key: "leadTime", label: "Lead Time", type: "days" },
  { key: "incoterm", label: "Incoterm" },
  { key: "items", label: "Items", type: "int" },
  { key: "otif", label: "OTIF%", type: "pct" },
  { key: "spend", label: "Spend", type: "money" },
  {
    key: "grade",
    label: "Grade",
    render: (val) => {
      if (!val) return "\u2014";
      return <Badge text={val} />;
    },
  },
  { key: "currency", label: "Currency" },
  { key: "paymentTerms", label: "Payment Terms" },
  { key: "contact", label: "Contact" },
];

/* ------------------------------------------------------------------ */
/* New vendor form initial state                                       */
/* ------------------------------------------------------------------ */

const EMPTY_VENDOR_FORM = {
  vendor_no: "",
  name: "",
  city: "",
  country_code: "",
  currency_code: "",
  email: "",
  phone: "",
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Vendors() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedCompany } = useCompany();

  /* ---- Data state ---- */
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [error, setError] = useState(null);

  /* ---- Filter state ---- */
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [riskFilter, setRiskFilter] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");

  /* ---- Modal state ---- */
  const [showAddModal, setShowAddModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newVendor, setNewVendor] = useState({ ...EMPTY_VENDOR_FORM });

  /* ---- Fetch vendors ---- */
  const fetchVendors = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Map company IDs to exact names used by backend API
      const companyMap = {
        "ysu": "YSU Marketing Corporation",
        "cody-marketing": "Cody Marketing Corporation", 
        "jmu-cebu": "JMU Marketing Inc.- Cebu",
        "jmu": "JMU Marketing Inc.",
      };
      
      const companyFilter = selectedCompany?.id !== "all" 
        ? companyMap[selectedCompany?.id] || selectedCompany?.name 
        : undefined;
        
      console.log("[Vendors] Sending company filter:", companyFilter);
      
      const data = await getVendors({
        search: search || undefined,
        company: companyFilter,
        pageSize: 1000,
      });

      /* DB API returns { total, rows } */
      const vendors = data.rows || data || [];

      /* Map DB records to display rows */
      const mapped = vendors.map((v) => ({
        id: v.number,
        vendorNo: v.number,
        name: v.displayName,
        category: v.vendorPostingGroup || "\u2014",
        tier: "",
        status: v.blocked && v.blocked !== " " && v.blocked !== "" ? "Blocked" : "Active",
        approval: "Approved",
        risk: "",
        leadTime: 7,
        incoterm: "FOB",
        items: 0,
        otif: 0,
        spend: 0,
        grade: "",
        currency: v.currencyCode || "PHP",
        paymentTerms: v.paymentTermsCode || "NET30",
        contact: v.email || v.phoneNumber || "\u2014",
      }));

      setVendors(mapped);
    } catch (err) {
      console.error("Vendors fetch error:", err);
      const message = err?.response?.data?.error?.message || err.message || "Failed to load vendors.";
      setError(message);
      toast(message, "bad");
    } finally {
      setLoading(false);
    }
  }, [search, toast, selectedCompany]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  /* ---- Filtered vendors ---- */
  const filteredVendors = useMemo(() => {
    return vendors.filter((v) => {
      if (categoryFilter && v.category !== categoryFilter) return false;
      if (statusFilter && v.status !== statusFilter) return false;
      if (riskFilter && v.risk !== riskFilter) return false;
      if (approvalFilter && v.approval !== approvalFilter) return false;
      if (tierFilter && v.tier !== tierFilter) return false;
      return true;
    });
  }, [vendors, categoryFilter, statusFilter, riskFilter, approvalFilter, tierFilter]);

  /* ---- Check if any filter is active ---- */
  const hasFilters =
    categoryFilter || statusFilter || riskFilter || approvalFilter || tierFilter;

  /* ---- Clear all filters ---- */
  function handleClearFilters() {
    setCategoryFilter("");
    setStatusFilter("");
    setRiskFilter("");
    setApprovalFilter("");
    setTierFilter("");
  }

  /* ---- Export handler ---- */
  function handleExport() {
    const headers = VENDOR_COLUMNS.map((c) => c.label);
    const csvRows = filteredVendors.map((v) =>
      VENDOR_COLUMNS.map((c) => {
        const val = v[c.key];
        return val != null ? String(val) : "";
      }).join(",")
    );

    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vendors_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast("Vendors exported successfully", "good");
  }

  /* ---- New vendor form handlers ---- */
  function handleNewVendorChange(field, value) {
    setNewVendor((prev) => ({ ...prev, [field]: value }));
  }

  async function handleCreateVendor() {
    if (!newVendor.vendor_no.trim() || !newVendor.name.trim()) {
      toast("Vendor No. and Name are required", "warn");
      return;
    }

    setCreating(true);
    try {
      await createVendor({
        number: newVendor.vendor_no.trim(),
        displayName: newVendor.name.trim(),
        city: newVendor.city.trim(),
        countryRegionCode: newVendor.country_code.trim(),
        currencyCode: newVendor.currency_code.trim(),
        email: newVendor.email.trim(),
        phoneNumber: newVendor.phone.trim(),
      });

      toast(`Vendor "${newVendor.name}" created successfully`, "good");
      setShowAddModal(false);
      setNewVendor({ ...EMPTY_VENDOR_FORM });
      fetchVendors();
    } catch (err) {
      console.error("Create vendor error:", err);
      const message = err?.response?.data?.error?.message || err.message || "Failed to create vendor.";
      toast(message, "bad");
    } finally {
      setCreating(false);
    }
  }

  /* ---- Render ---- */
  return (
    <div>
      <Loading show={loading && vendors.length === 0} text="Loading vendors..." />

      {/* Page header */}
      <div style={styles.header}>
        <div>
          <h1>Vendors</h1>
          <p className="muted" style={{ marginTop: 2 }}>
            {filteredVendors.length} vendor{filteredVendors.length !== 1 ? "s" : ""}
            {hasFilters ? ` (filtered from ${vendors.length})` : ""}
          </p>
        </div>
        <div style={styles.headerActions}>
          <button className="btn" onClick={handleExport} disabled={filteredVendors.length === 0}>
            <Download size={14} />
            Export
          </button>
          <button className="btn primary" onClick={() => setShowAddModal(true)}>
            <Plus size={14} />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && !loading && (
        <div style={styles.errorBanner}>
          <strong>Error:</strong> {error}
          <button className="btn tiny" onClick={fetchVendors} style={{ marginLeft: 12 }}>
            Retry
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: "12px 16px" }}>
          <div style={styles.filterBar}>
            {/* Search */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Search</label>
              <input
                type="text"
                placeholder="Name or vendor no..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={styles.filterInput}
              />
            </div>

            {/* Category */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={styles.filterSelect}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || "All Categories"}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={styles.filterSelect}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || "All Statuses"}
                  </option>
                ))}
              </select>
            </div>

            {/* Risk Rating */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Risk</label>
              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                style={styles.filterSelect}
              >
                {RISK_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || "All Ratings"}
                  </option>
                ))}
              </select>
            </div>

            {/* Approval Status */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Approval</label>
              <select
                value={approvalFilter}
                onChange={(e) => setApprovalFilter(e.target.value)}
                style={styles.filterSelect}
              >
                {APPROVAL_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || "All Approvals"}
                  </option>
                ))}
              </select>
            </div>

            {/* Strategic Tier */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Tier</label>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                style={styles.filterSelect}
              >
                {TIER_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || "All Tiers"}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear filters */}
            {hasFilters && (
              <button className="btn tiny ghost" onClick={handleClearFilters} style={{ alignSelf: "flex-end" }}>
                <X size={12} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Vendors table */}
      <div className="card">
        <div className="card-head">
          <h3>All Vendors</h3>
          <span className="muted">{filteredVendors.length} records</span>
        </div>
        <div className="card-body flush">
          <DataTable
            columns={VENDOR_COLUMNS}
            rows={filteredVendors}
            onRow={(row) => navigate(`/vendors/${row.vendorNo}`)}
            emptyText={hasFilters ? "No vendors match the current filters" : "No vendors found"}
          />
        </div>
      </div>

      {/* Add Vendor Modal */}
      {showAddModal && (
        <Modal
          title="Add New Vendor"
          onClose={() => {
            if (!creating) {
              setShowAddModal(false);
              setNewVendor({ ...EMPTY_VENDOR_FORM });
            }
          }}
          footer={
            <>
              <button
                className="btn"
                onClick={() => {
                  setShowAddModal(false);
                  setNewVendor({ ...EMPTY_VENDOR_FORM });
                }}
                disabled={creating}
              >
                Cancel
              </button>
              <button className="btn primary" onClick={handleCreateVendor} disabled={creating}>
                {creating ? "Creating..." : "Create Vendor"}
              </button>
            </>
          }
        >
          <div style={styles.formGrid}>
            <div style={styles.formField}>
              <label style={styles.formLabel}>
                Vendor No. <span style={{ color: "var(--bad)" }}>*</span>
              </label>
              <input
                type="text"
                value={newVendor.vendor_no}
                onChange={(e) => handleNewVendorChange("vendor_no", e.target.value)}
                placeholder="e.g. V00100"
                style={styles.formInput}
                required
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>
                Name <span style={{ color: "var(--bad)" }}>*</span>
              </label>
              <input
                type="text"
                value={newVendor.name}
                onChange={(e) => handleNewVendorChange("name", e.target.value)}
                placeholder="Vendor display name"
                style={styles.formInput}
                required
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>City</label>
              <input
                type="text"
                value={newVendor.city}
                onChange={(e) => handleNewVendorChange("city", e.target.value)}
                placeholder="City"
                style={styles.formInput}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Country Code</label>
              <input
                type="text"
                value={newVendor.country_code}
                onChange={(e) => handleNewVendorChange("country_code", e.target.value.toUpperCase())}
                placeholder="e.g. PH"
                maxLength={10}
                style={styles.formInput}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Currency Code</label>
              <input
                type="text"
                value={newVendor.currency_code}
                onChange={(e) => handleNewVendorChange("currency_code", e.target.value.toUpperCase())}
                placeholder="e.g. PHP, USD"
                maxLength={10}
                style={styles.formInput}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Email</label>
              <input
                type="email"
                value={newVendor.email}
                onChange={(e) => handleNewVendorChange("email", e.target.value)}
                placeholder="contact@vendor.com"
                style={styles.formInput}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Phone</label>
              <input
                type="tel"
                value={newVendor.phone}
                onChange={(e) => handleNewVendorChange("phone", e.target.value)}
                placeholder="+63 xxx xxx xxxx"
                style={styles.formInput}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    gap: 8,
    flexShrink: 0,
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 14,
    alignItems: "flex-end",
    marginBottom: 16,
    padding: "14px 16px",
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 160,
  },
  filterLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--ink-3)",
  },
  filterInput: {
    font: "inherit",
    fontSize: 13,
    padding: "9px 12px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    background: "var(--bg)",
    color: "var(--ink)",
    outline: "none",
    minWidth: 180,
    width: "100%",
  },
  filterSelect: {
    font: "inherit",
    fontSize: 13,
    padding: "9px 12px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    background: "var(--bg)",
    color: "var(--ink)",
    outline: "none",
    cursor: "pointer",
    minWidth: 160,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  formField: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  formLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--ink-2)",
  },
  formInput: {
    font: "inherit",
    fontSize: 13,
    padding: "9px 12px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    background: "var(--panel)",
    color: "var(--ink)",
    outline: "none",
  },
  errorBanner: {
    padding: "12px 16px",
    marginBottom: 16,
    background: "var(--bad-soft)",
    border: "1px solid var(--bad)",
    borderRadius: "var(--radius-sm)",
    color: "var(--bad)",
    fontSize: 13,
    fontWeight: 550,
    display: "flex",
    alignItems: "center",
  },
};
